import {
  ChartFrame,
  Histogram,
  histogramBins,
  Legend,
  LineLayer,
  type HistogramMarker,
} from '@finsim/charts';
import type { Goal } from '@finsim/model';
import { useUnit } from 'effector-react';
import { Dices, Loader2, Plus, Target } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { McMetric } from '../../../entities/simulation';
import { simModeChanged } from '../../../entities/view-settings';
import { goalAddRequested } from '../../../features/edit-assumptions';
import { Button, EmptyState, Segmented } from '../../../shared/ui';
import { scenarioColor } from '../lib/series';
import { ChartCard } from './chart-card';
import { FanChart } from './fan-chart';
import { useFanTable } from './fan-table';
import { KpiStrip } from './kpi-strip';
import { NoUncertaintyNote } from './mc-note';
import type { CanvasData } from './use-canvas';

const HISTOGRAM_BINS = 30;

/** Monte Carlo view: range of outcomes, chance of running out of cash, distribution and goals. */
export function Risk({ data }: { data: CanvasData }) {
  const { t } = useTranslation();
  const enable = useUnit(simModeChanged);

  if (data.simMode !== 'monteCarlo') {
    return (
      <EmptyState
        icon={<Dices />}
        title={t('risk.enableTitle')}
        body={t('risk.enableBody')}
        action={
          <Button
            variant="primary"
            onClick={() => {
              enable('monteCarlo');
            }}
          >
            {t('risk.enable')}
          </Button>
        }
        className="flex-1 py-24"
      />
    );
  }
  if (!data.mc || !data.focused || !data.focusedMc) {
    return (
      <EmptyState
        icon={<Loader2 className="animate-spin" />}
        title={t('mc.pending')}
        className="flex-1 py-24"
      />
    );
  }
  return <RiskContent data={data} />;
}

function RiskContent({ data }: { data: CanvasData }) {
  const { t } = useTranslation();
  const [metric, setMetric] = useState<McMetric>('netWorth');
  const [hover, setHover] = useState(-1);
  const focused = data.focused;
  const focusedList = useMemo(() => (focused ? [focused] : []), [focused]);
  const metricLabel = metric === 'netWorth' ? t('charts.netWorth') : t('charts.cash');
  const fanTitle = t('risk.fanTitle', { metric: metricLabel });
  const fanTable = useFanTable(data, metric, focusedList, fanTitle);
  if (!focused) return null;

  return (
    <div className="space-y-4 p-4">
      <NoUncertaintyNote data={data} />
      <KpiStrip data={data} />
      <ChartCard
        title={fanTitle}
        subtitle={[focused.name, t('mc.bandsCaption'), data.moneyCaption]
          .filter(Boolean)
          .join(' · ')}
        table={fanTable}
        actions={
          <Segmented
            label={t('risk.metric')}
            value={metric}
            onValueChange={setMetric}
            options={[
              { value: 'netWorth', label: t('charts.netWorth') },
              { value: 'cash', label: t('charts.cash') },
            ]}
          />
        }
      >
        <FanChart
          data={data}
          title={t('risk.fanAria', { metric: metricLabel, name: focused.name })}
          metric={metric}
          scenarios={focusedList}
          height={280}
          hover={hover}
          onHover={setHover}
        />
      </ChartCard>
      <div className="grid gap-4 xl:grid-cols-2">
        <CashNegativeCard data={data} />
        <DistributionCard data={data} hover={hover} />
      </div>
      <GoalsCard data={data} />
    </div>
  );
}

/** Share of paths with negative cash over time, per visible variant. */
function CashNegativeCard({ data }: { data: CanvasData }) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<'month' | 'ever'>('month');
  const [hover, setHover] = useState(-1);
  const { mc, visible, dates, range, buckets, fmt } = data;
  const lines = visible.flatMap((scenario) => {
    const r = mc?.scenarios[scenario.id];
    if (!r) return [];
    return [{ scenario, values: mode === 'month' ? r.pCashNegative : r.pCashNegativeEver }];
  });

  return (
    <ChartCard
      title={t('risk.pCashNegative')}
      subtitle={mode === 'month' ? t('risk.inMonth') : t('risk.ever')}
      legend={
        lines.length > 1 ? (
          <Legend
            label={t('charts.legend')}
            items={lines.map((l) => ({
              id: l.scenario.id,
              label: l.scenario.name,
              color: scenarioColor(l.scenario),
            }))}
          />
        ) : undefined
      }
      actions={
        <Segmented
          label={t('risk.pCashNegative')}
          value={mode}
          onValueChange={setMode}
          options={[
            { value: 'month', label: t('risk.inMonth') },
            { value: 'ever', label: t('risk.ever') },
          ]}
        />
      }
      table={{
        caption: t('risk.pCashNegative'),
        columns: [t('charts.year'), ...lines.map((l) => l.scenario.name)],
        rows: buckets.map((b) => ({
          label: String(b.year),
          values: lines.map((l) => fmt.probability(l.values[b.to] ?? 0)),
        })),
      }}
    >
      <ChartFrame
        markers={data.markers}
        dates={dates}
        range={range}
        height={200}
        yDomain={[0, 1]}
        formatY={fmt.percent}
        formatXTick={fmt.year}
        ariaLabel={t('risk.pCashNegativeAria')}
        hoverIndex={hover}
        onHoverChange={setHover}
        renderTooltip={(i) => (
          <div className="space-y-1">
            <p className="text-text-3">{dates[i] ? fmt.monthLong(dates[i]) : ''}</p>
            {lines.map(({ scenario, values }) => (
              <p key={scenario.id} className="flex items-center justify-between gap-4">
                <span className="flex items-center gap-1.5 text-text-2">
                  <span
                    aria-hidden
                    className="h-0.5 w-2.5 rounded-full"
                    style={{ background: scenarioColor(scenario) }}
                  />
                  {scenario.name}
                </span>
                <span className="tabular font-medium">{fmt.probability(values[i] ?? 0)}</span>
              </p>
            ))}
          </div>
        )}
      >
        {lines.map(({ scenario, values }) => (
          <LineLayer key={scenario.id} values={values} color={scenarioColor(scenario)} />
        ))}
      </ChartFrame>
    </ChartCard>
  );
}

/** Net worth across paths at the end of the hovered year (the last month by default). */
function DistributionCard({ data, hover }: { data: CanvasData; hover: number }) {
  const { t } = useTranslation();
  const { focused, focusedMc, mc, valueMode, dates, fmt } = data;
  const samples = focusedMc?.samples;
  const months = samples?.months ?? [];
  const index = hover < 0 ? months.length - 1 : months.findIndex((m) => m >= hover);
  const k = index < 0 ? months.length - 1 : index;
  const month = months[k] ?? 0;
  const values = samples?.netWorth[valueMode][k];
  const bands = focusedMc?.bands.netWorth[valueMode];
  const date = dates[month];
  const markers: HistogramMarker[] = bands
    ? [
        { value: bands.p10[month] ?? 0, label: 'P10' },
        { value: bands.p50[month] ?? 0, label: 'P50' },
        { value: bands.p90[month] ?? 0, label: 'P90' },
      ]
    : [];
  const bins = useMemo(() => (values ? histogramBins(values, HISTOGRAM_BINS) : []), [values]);
  if (!values || !focused || !mc || !date) return null;
  const binLabel = (from: number, to: number, share: number): string =>
    t('risk.bin', { from: fmt.compact(from), to: fmt.compact(to), share: fmt.probability(share) });

  return (
    <ChartCard
      title={t('risk.histogramTitle')}
      subtitle={[t('risk.histogramAt', { date: fmt.year(date) }), data.moneyCaption]
        .filter(Boolean)
        .join(' · ')}
      table={{
        caption: t('risk.histogramTitle'),
        columns: [t('charts.netWorth'), t('charts.share')],
        rows: bins.map((b) => ({
          label: t('mc.range', { from: fmt.compact(b.from), to: fmt.compact(b.to) }),
          values: [fmt.probability(b.count / values.length)],
        })),
      }}
    >
      <Histogram
        values={values}
        bins={HISTOGRAM_BINS}
        height={200}
        color={`color-mix(in srgb, ${scenarioColorOf(data)} 70%, transparent)`}
        formatX={fmt.compact}
        formatBin={binLabel}
        markers={markers}
        ariaLabel={t('risk.histogramAria', {
          paths: fmt.count(mc.paths),
          date: fmt.year(date),
          name: focused.name,
        })}
      />
      <p className="pb-1 text-xs text-text-3">{t('risk.histogramHint')}</p>
    </ChartCard>
  );
}

const scenarioColorOf = (data: CanvasData): string =>
  data.focused ? scenarioColor(data.focused) : 'var(--chart-series-1)';

/** Probability of each goal per visible variant. */
function GoalsCard({ data }: { data: CanvasData }) {
  const { t } = useTranslation();
  const add = useUnit(goalAddRequested);
  const { project, visible, mc, dates, fmt } = data;
  const goals = project.goals;
  const describe = (goal: Goal): string => {
    const at = dates[Math.min(goal.by, dates.length - 1)];
    const values = {
      metric: t(`goals.metrics.${goal.metric}`),
      target: fmt.money(goal.target),
      date: at ? fmt.monthLong(at) : '',
    };
    return goal.real ? t('risk.goalLineReal', values) : t('risk.goalLine', values);
  };

  return (
    <ChartCard
      title={t('risk.goals')}
      actions={
        <Button
          size="sm"
          variant="ghost"
          leadingIcon={<Plus />}
          onClick={() => {
            add({ name: t('goals.newName', { n: goals.length + 1 }) });
          }}
        >
          {t('risk.addGoal')}
        </Button>
      }
    >
      {goals.length === 0 ? (
        <EmptyState
          icon={<Target />}
          title={t('risk.goals')}
          body={t('risk.goalsEmpty')}
          className="py-6"
        />
      ) : (
        <ul className="divide-y divide-border-subtle pb-1">
          {goals.map((goal) => (
            <li
              key={goal.id}
              className="grid gap-2 py-2.5 md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]"
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">{goal.name}</p>
                <p className="text-xs text-text-3">{describe(goal)}</p>
              </div>
              <ul className="space-y-1">
                {visible.map((scenario) => {
                  const chance = mc?.scenarios[scenario.id]?.goals[goal.id] ?? 0;
                  return (
                    <li
                      key={scenario.id}
                      className="grid grid-cols-[minmax(0,7rem)_1fr_3rem] items-center gap-2 text-xs"
                      aria-label={t('risk.goalChanceAria', {
                        name: scenario.name,
                        chance: fmt.probability(chance),
                      })}
                    >
                      <span className="truncate text-text-2">{scenario.name}</span>
                      <span aria-hidden className="h-1.5 overflow-hidden rounded-full bg-bg-3">
                        <span
                          className="block h-full rounded-full"
                          style={{
                            width: `${String(chance * 100)}%`,
                            background: scenarioColor(scenario),
                          }}
                        />
                      </span>
                      <span className="tabular text-right font-medium">
                        {fmt.probability(chance)}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </ChartCard>
  );
}
